import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { ClassicPreset, NodeEditor } from 'rete';
import { AngularPlugin, NodeComponent, Presets } from 'rete-angular-plugin/20';
import { AreaExtensions, AreaPlugin } from 'rete-area-plugin';
import {
  Presets as ArrangePresets,
  AutoArrangePlugin,
} from 'rete-auto-arrange-plugin';
import {
  ProductType,
  productTypes,
} from 'src/app/services/product-service/product-service.service';
import { StateNode } from './state-node/state-node';
import { StateNodeComponent } from './state-node/state-node.component';
import { TransitionNode } from './transition-node/transition-node';
import { TransitionNodeComponent } from './transition-node/transition-node.component';
import {
  AreaExtra,
  Schemes,
  WorkflowProcess,
  WorkflowState,
  WorkflowTransition,
} from './types';
import {
  Partner,
  partners,
  WorkflowEditorService,
} from './workflow-editor.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AxiosError } from 'axios';
import { PayeeDropdownComponent } from '../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { FormsModule } from '@angular/forms';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { MatIcon } from '@angular/material/icon';
import { PayeeInfoComponent } from '../../../shared/payee-info/payee-info.component';
import { AsyncPipe } from '@angular/common';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';

export const socket = new ClassicPreset.Socket('socket');

export type WorkflowNodeSearchOption =
  | { kind: 'state'; state: WorkflowState }
  | { kind: 'transition'; transition: WorkflowTransition };

@Component({
  selector: 'app-workflow-editor',
  templateUrl: './workflow-editor.component.html',
  styleUrls: ['./workflow-editor.component.scss'],
  imports: [
    PayeeDropdownComponent,
    FormsModule,
    PayeeIconButtonComponent,
    MatIcon,
    PayeeInfoComponent,
    AsyncPipe,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
  ],
})
export class WorkflowEditorComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private injector = inject(Injector);
  private workflowEditorService = inject(WorkflowEditorService);
  private snackbar = inject(MatSnackBar);

  private readonly destroy = new Subject<void>();

  private readonly activeStates = new Set<WorkflowState>();
  private area!: AreaPlugin<Schemes, AreaExtra>;
  private arrange!: AutoArrangePlugin<Schemes>;
  private editor!: NodeEditor<Schemes>;
  private render!: AngularPlugin<Schemes, AreaExtra>;

  @ViewChild('reteContainer') reteContainer!: ElementRef<HTMLElement>;

  readonly partners: string[] = [...partners];
  partner: Partner = 'any';

  readonly productTypes: ProductType[] = [...productTypes];
  product: ProductType = 'notice';

  loading = false;
  workflowProcess?: WorkflowProcess;
  nodeSearchOptions: WorkflowNodeSearchOption[] = [];
  selectedNodeSearch: WorkflowNodeSearchOption | null = null;

  get forceExpand() {
    return this.workflowEditorService.forceExpand;
  }
  get showInfoNotifications() {
    return this.workflowEditorService.showInfoNotifications;
  }
  get fogOfWar() {
    return this.workflowEditorService.fogOfWar;
  }
  get immersiveCanvas() {
    return this.workflowEditorService.immersiveCanvas;
  }

  ngOnInit(): void {
    this.workflowEditorService.stateChange
      .pipe(takeUntil(this.destroy))
      .subscribe(async (change) => {
        if (change.selected) {
          this.activeStates.add(change.node.state);
        } else {
          this.activeStates.delete(change.node.state);
        }
      });

    this.workflowEditorService.stateChange
      .pipe(takeUntil(this.destroy), debounceTime(100))
      .subscribe(async () => {
        if (this.fogOfWar.value) {
          await this.updateFogOfWar();
        }
      });
  }

  async ngAfterViewInit(): Promise<void> {
    this.editor = new NodeEditor<Schemes>();
    this.area = new AreaPlugin<Schemes, AreaExtra>(
      this.reteContainer.nativeElement,
    );
    this.render = new AngularPlugin<Schemes, AreaExtra>({
      injector: this.injector,
    });
    this.arrange = new AutoArrangePlugin<Schemes>();

    this.arrange.addPreset(ArrangePresets.classic.setup());
    this.render.addPreset(
      Presets.classic.setup({
        customize: {
          node(context) {
            if (context.payload instanceof StateNode) {
              return StateNodeComponent;
            } else if (context.payload instanceof TransitionNode) {
              return TransitionNodeComponent;
            } else {
              return NodeComponent;
            }
          },
        },
      }),
    );

    this.area.use(this.arrange);
    this.area.use(this.render);
    this.editor.use(this.area);

    await this.resetWorkflowProcesses();
  }

  ngOnDestroy(): void {
    this.workflowEditorService.immersiveCanvas.next(false);
    this.area?.destroy();
    this.destroy.next();
    this.destroy.complete();
  }

  toggleImmersive(): void {
    const s = this.workflowEditorService.immersiveCanvas;
    s.next(!s.value);
  }

  async resetWorkflowProcesses(): Promise<void> {
    try {
      this.loading = true;
      this.workflowProcess =
        await this.workflowEditorService.getWorkflowProcess(
          this.product,
          this.partner,
        );
    } catch (error) {
      let message = 'Error while getting workflow process';
      console.error('Error while getting workflow process', error);

      if (error instanceof AxiosError && error.response?.data?.message) {
        message = error.response.data.message;
      }

      this.snackbar.open(message, 'OK', { duration: 10_000 });
      return;
    } finally {
      this.loading = false;
    }

    if (!this.workflowProcess) {
      this.nodeSearchOptions = [];
      this.selectedNodeSearch = null;
      return;
    }

    this.activeStates.clear();
    this.selectedNodeSearch = null;
    this.nodeSearchOptions = this.buildNodeSearchOptions(this.workflowProcess);
    this.selectEntrypoint();
    await this.updateFogOfWar();
  }

  readonly nodeSearchDisplayWith = (
    option?: WorkflowNodeSearchOption | null,
  ): string => {
    if (!option) {
      return '';
    }
    if (option.kind === 'state') {
      return `Állapot: ${option.state.label}`;
    }
    return `Átmenet: ${option.transition.label}`;
  };

  readonly nodeSearchFilterPredicate = (
    option: unknown,
    filter: string,
  ): boolean => {
    if (
      !option ||
      typeof option !== 'object' ||
      !('kind' in option)
    ) {
      return false;
    }

    const nodeOption = option as WorkflowNodeSearchOption;
    const needle = filter.toLowerCase();
    const haystacks =
      nodeOption.kind === 'state'
        ? [
            nodeOption.state.label,
            nodeOption.state.class_name,
            nodeOption.state.id,
            'állapot',
            'state',
          ]
        : [
            nodeOption.transition.label,
            nodeOption.transition.class_name,
            nodeOption.transition.id,
            nodeOption.transition.log_label,
            nodeOption.transition.group,
            'átmenet',
            'transition',
          ];

    return haystacks.some((value) => value.toLowerCase().includes(needle));
  };

  async onNodeSearchSelect(
    option: WorkflowNodeSearchOption | null,
  ): Promise<void> {
    if (!option || !this.workflowProcess) {
      return;
    }

    if (option.kind === 'state') {
      await this.activateStateNode(option.state);
      return;
    }

    await this.activateTransitionNode(option.transition);
  }

  async createWorkflowProcess() {
    if (!this.workflowProcess) {
      return;
    }

    await Promise.allSettled(
      this.workflowProcess.states.map((s) => this.addStateNode(s)),
    );
    await Promise.allSettled(
      this.workflowProcess.transitions.map((s) => this.addTransitionNode(s)),
    );
  }

  async addStateNode(state: WorkflowState): Promise<StateNode | undefined> {
    try {
      const node = new StateNode(state, this.editor);
      await this.editor.addNode(node);
      return node;
    } catch (error) {
      console.warn('Error while adding state node', error);
    }
  }

  async addTransitionNode(transition: WorkflowTransition) {
    if (!this.showInfoNotifications.value && transition.is_info_notification) {
      return;
    }

    try {
      const node = new TransitionNode(transition, this.editor);
      await node.setupConnections();
      if (node.from.length || node.to) {
        await this.editor.addNode(node);
      }
    } catch (error) {
      console.warn('Error while adding transition node', error);
    }
  }

  async removeNode(node: TransitionNode) {
    await Promise.allSettled(
      node.connections.map((c) => this.editor.removeConnection(c.id)),
    );
    await this.editor.removeNode(node.id);
  }

  async resetZoom(): Promise<void> {
    await new Promise<void>((res) =>
      setTimeout(async () => {
        await this.arrange.layout();
        AreaExtensions.zoomAt(this.area, this.editor.getNodes());
        res();
      }, 100),
    );
  }

  async toggleForceExpand(): Promise<void> {
    this.forceExpand.next(this.forceExpand.value !== true);
    await this.resetZoom();
  }

  async toggleInfoNotifications(): Promise<void> {
    if (!this.workflowProcess) {
      return;
    }

    this.showInfoNotifications.next(!this.showInfoNotifications.value);

    if (this.fogOfWar.value) {
      await this.updateFogOfWar();
      return;
    }

    if (!this.showInfoNotifications.value) {
      await Promise.allSettled(
        this.editor.getNodes().map(async (node) => {
          if (
            !(node instanceof TransitionNode) ||
            !node.transition.is_info_notification
          ) {
            return;
          }

          await this.removeNode(node);
        }),
      );
    } else {
      await Promise.allSettled(
        this.workflowProcess.transitions.map(async (transition) => {
          if (!transition.is_info_notification) {
            return;
          }

          await this.addTransitionNode(transition);
        }),
      );
    }

    await this.resetZoom();
  }

  async toggleFogOfWar(): Promise<void> {
    if (!this.workflowProcess) {
      return;
    }

    this.fogOfWar.next(!this.fogOfWar.value);
    await this.updateFogOfWar();
  }

  workflowProcessDisplayWith(workflowProcess?: WorkflowProcess): string {
    return workflowProcess?.id ?? '';
  }

  private buildNodeSearchOptions(
    workflowProcess: WorkflowProcess,
  ): WorkflowNodeSearchOption[] {
    const states: WorkflowNodeSearchOption[] = workflowProcess.states.map(
      (state) => ({ kind: 'state', state }),
    );
    const transitions: WorkflowNodeSearchOption[] =
      workflowProcess.transitions.map((transition) => ({
        kind: 'transition',
        transition,
      }));

    return [...states, ...transitions].sort((a, b) =>
      this.nodeSearchDisplayWith(a).localeCompare(
        this.nodeSearchDisplayWith(b),
        'hu',
      ),
    );
  }

  private async activateStateNode(state: WorkflowState): Promise<void> {
    this.activeStates.clear();
    this.activeStates.add(state);
    await this.updateFogOfWar();

    const node = this.editor
      .getNodes()
      .find(
        (n): n is StateNode => n instanceof StateNode && n.state.id === state.id,
      );

    if (node) {
      await AreaExtensions.zoomAt(this.area, [node]);
    }
  }

  private async activateTransitionNode(
    transition: WorkflowTransition,
  ): Promise<void> {
    if (
      transition.is_info_notification &&
      !this.showInfoNotifications.value
    ) {
      this.showInfoNotifications.next(true);
    }

    if (this.fogOfWar.value) {
      this.activeStates.clear();

      if (transition.from_process_states?.length) {
        for (const stateId of transition.from_process_states) {
          const state = this.workflowProcess?.states.find(
            (s) => s.id === stateId,
          );
          if (state) {
            this.activeStates.add(state);
          }
        }
      } else if (transition.to_process_state) {
        const toState = this.workflowProcess?.states.find(
          (s) => s.id === transition.to_process_state,
        );
        if (toState) {
          this.activeStates.add(toState);
        }
      }

      if (this.activeStates.size === 0) {
        this.selectEntrypoint();
      }

      await this.updateFogOfWar();
    } else if (transition.is_info_notification) {
      await this.updateFogOfWar();
    }

    let node = this.editor
      .getNodes()
      .find(
        (n): n is TransitionNode =>
          n instanceof TransitionNode && n.transition.id === transition.id,
      );

    if (!node) {
      await this.addTransitionNode(transition);
      node = this.editor
        .getNodes()
        .find(
          (n): n is TransitionNode =>
            n instanceof TransitionNode && n.transition.id === transition.id,
        );
      if (node) {
        await this.arrange.layout();
      }
    }

    if (node) {
      await AreaExtensions.zoomAt(this.area, [node]);
    }
  }

  private async updateFogOfWar(): Promise<void> {
    await this.editor.clear();
    if (this.fogOfWar.value) {
      const activeStates = [...this.activeStates.values()];

      await Promise.allSettled(
        activeStates.map(async (state) => {
          const node = await this.addStateNode(state);
          if (node) {
            node.is_active = true;
          }
        }),
      );

      if (!this.workflowProcess) {
        return;
      }

      const transitions = this.workflowProcess.transitions.filter(
        (t) =>
          !t.from_process_states ||
          activeStates.some((s) => t.from_process_states?.includes(s.id)),
      );

      const states = this.workflowProcess.states.filter(
        (s) =>
          !this.activeStates.has(s) &&
          transitions.some((t) => t.to_process_state === s.id),
      );

      await Promise.allSettled(states.map((s) => this.addStateNode(s)));
      await Promise.allSettled(
        transitions.map((t) => this.addTransitionNode(t)),
      );
    } else {
      await this.createWorkflowProcess();
      await Promise.allSettled(
        this.editor.getNodes().map(async (node) => {
          if (node instanceof StateNode && this.activeStates.has(node.state)) {
            node.is_active = true;
          }
        }),
      );
    }
    await this.resetZoom();
  }

  private selectEntrypoint(): void {
    if (!this.workflowProcess) {
      return;
    }

    for (const state of this.workflowProcess.states) {
      if (state.is_entry_point && state.is_start) {
        this.activeStates.add(state);
      }
    }
  }
}
